import { type ProspectContactDto } from "@crowdcoursing/api/openapi";
import { Delete, Edit, Work } from "@mui/icons-material";
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { EmailWithCopyIcon } from "~ui/molecules/EmailWithCopyIcon/EmailWithCopyIcon";
import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";

const tPrimary = "Primary";
const tAdded = "Added";
const tUpdated = "Updated";
const tEditContact = "Edit contact";
const tDeleteContact = "Delete contact";

interface ContactCardProps {
  contact: ProspectContactDto;
  onEdit: (contact: ProspectContactDto) => void;
  onDelete: (contact: ProspectContactDto) => void;
}

export const ContactCard = ({ contact, onEdit, onDelete }: ContactCardProps) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        alignItems: "start",
        display: "grid",
        gap: 1,
        gridTemplateColumns: "1fr auto",
        p: 2,
      }}
    >
      <Box display="grid" gap={1}>
        {/* Name and Role */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}
            {contact.isPrimary && <Chip label={tPrimary} color="primary" size="small" sx={{ ml: 1 }} />}
          </Typography>
          {contact.roleTitle && (
            <Typography variant="body2" color="text.secondary" sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
              <Work fontSize="small" />
              {contact.roleTitle}
            </Typography>
          )}
        </Box>

        {/* Contact Info */}
        <Stack spacing={1}>
          {contact.email && <EmailWithCopyIcon email={contact.email} />}
          {contact.phone && <PhoneWithCopyIcon phoneNumber={contact.phone} />}
        </Stack>

        {/* Notes */}
        {contact.notes && (
          <Typography variant="body2" color="text.secondary">
            {contact.notes}
          </Typography>
        )}

        {/* Timestamp */}
        <Typography variant="caption" color="text.secondary">
          {tAdded} {DateTime.fromISO(contact.createdAt ?? "").toRelative()}
          {contact.updatedAt !== contact.createdAt &&
            ` • ${tUpdated} ${DateTime.fromISO(contact.updatedAt ?? "").toRelative()}`}
        </Typography>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={1}>
        <Tooltip title={tEditContact}>
          <IconButton size="small" onClick={() => onEdit(contact)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={tDeleteContact}>
          <IconButton size="small" color="error" onClick={() => onDelete(contact)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
};
